/* ODX Featured collection — mobile teach hints (homepage experiment)
 * - Teaches horizontal product scrolling with a one-shot scrollLeft nudge + return
 * - Then subtly pulses the first visible card's Swiper prev/next (does not swipe images)
 *
 * Constraints:
 * - Mobile-only
 * - Respects prefers-reduced-motion
 * - Does not enable Swiper touch move
 */

(function () {
  'use strict';

  var scriptEl = document.currentScript;
  if (!scriptEl || scriptEl.getAttribute('data-odx-fc-teach') !== '1') return;

  function readBool(value, fallback) {
    if (value === true || value === false) return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return !!fallback;
  }

  function readNumber(value, fallback) {
    var n = typeof value === 'number' ? value : parseFloat(String(value || ''));
    return isFinite(n) ? n : fallback;
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isMobileViewport() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  function isHomeTemplate() {
    // Dawn sets `<body id="template-index">` on the homepage template.
    return document.body && document.body.id === 'template-index';
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function animateNumber(from, to, durationMs, onFrame, onDone) {
    var start = null;
    function tick(ts) {
      if (start == null) start = ts;
      var p = durationMs <= 0 ? 1 : clamp((ts - start) / durationMs, 0, 1);
      var eased = easeInOutCubic(p);
      onFrame(from + (to - from) * eased);
      if (p < 1) {
        window.requestAnimationFrame(tick);
      } else if (typeof onDone === 'function') {
        onDone();
      }
    }
    window.requestAnimationFrame(tick);
  }

  function getSlideStep(slider) {
    var slides = Array.from(slider.querySelectorAll('.slider__slide'));
    if (slides.length < 2) return 0;
    return Math.max(1, slides[1].offsetLeft - slides[0].offsetLeft);
  }

  function getPrimaryCardWidth(slider) {
    var firstSlide = slider.querySelector('.slider__slide');
    if (!firstSlide) return 0;
    var rect = firstSlide.getBoundingClientRect();
    return Math.max(0, Math.round(rect.width || 0));
  }

  function runProductRowTeach(slider, durationMs, done) {
    var start = slider.scrollLeft;
    var step = getSlideStep(slider);
    if (!step) return void (typeof done === 'function' && done());

    var maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
    if (maxScroll <= 1) return void (typeof done === 'function' && done());

    // Mobile teach distance target: ~45% of the visible card width.
    var cardWidth = getPrimaryCardWidth(slider) || step;
    var peek = Math.max(24, Math.round(cardWidth * 0.45));
    var target = clamp(start + peek, 0, maxScroll);
    if (Math.abs(target - start) < 2) return void (typeof done === 'function' && done());

    slider.classList.add('odx-fc-teach--nosnap');

    var outMs = Math.round(durationMs * 0.55);
    var backMs = Math.max(0, durationMs - outMs);

    animateNumber(
      start,
      target,
      outMs,
      function (v) {
        slider.scrollLeft = v;
      },
      function () {
        animateNumber(
          target,
          start,
          backMs,
          function (v) {
            slider.scrollLeft = v;
          },
          function () {
            slider.classList.remove('odx-fc-teach--nosnap');
            if (typeof done === 'function') done();
          },
        );
      },
    );
  }

  function pulseEl(el, totalMs) {
    if (!el) return;
    var cycles = 2;
    var slice = Math.max(120, Math.floor(totalMs / (cycles * 2)));
    var on = function () {
      el.classList.add('odx-fc-teach--pulse');
    };
    var off = function () {
      el.classList.remove('odx-fc-teach--pulse');
    };

    var i = 0;
    function step() {
      if (i >= cycles) {
        off();
        return;
      }
      on();
      window.setTimeout(function () {
        off();
        i += 1;
        window.setTimeout(step, Math.round(slice * 0.35));
      }, slice);
    }
    step();
  }

  function runInCardNavTeach(sectionRoot, durationMs) {
    var slider = sectionRoot.querySelector('[id^="Slider-"].slider--tablet.grid--peek');
    if (!slider) return;

    var slides = Array.from(slider.querySelectorAll('.slider__slide'));
    if (!slides.length) return;

    // Pick the slide closest to the viewport center (first visible card).
    var midX = window.innerWidth / 2;
    var best = null;
    var bestScore = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var s = slides[i];
      var r = s.getBoundingClientRect();
      var cx = r.left + r.width / 2;
      var score = Math.abs(cx - midX);
      if (score < bestScore) {
        bestScore = score;
        best = s;
      }
    }
    if (!best) return;

    var col = best.querySelector('.odx-card-media-column');
    if (!col) return;

    var nextBtn = col.querySelector('.swiper-button-next');
    var prevBtn = col.querySelector('.swiper-button-prev');
    if (!nextBtn && !prevBtn) return;

    // If there aren’t multiple real slides, skip (don’t teach a dead control).
    var swiperEl = col.querySelector('[id^="logos-"]');
    var slideCount = swiperEl ? swiperEl.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length : 0;
    if (!slideCount || slideCount < 2) return;

    // Pulse prev first (RTL-ish feel), then next — still subtle.
    pulseEl(prevBtn, durationMs);
    window.setTimeout(function () {
      pulseEl(nextBtn, durationMs);
    }, Math.round(durationMs * 0.35));
  }

  function boot() {
    var enabled = readBool(scriptEl.dataset.enabled, false);
    if (!enabled) return;

    var onlyHome = readBool(scriptEl.dataset.onlyHome, true);
    if (onlyHome && !isHomeTemplate()) return;

    if (!isMobileViewport()) return;
    if (prefersReducedMotion()) return;

    var sectionId = String(scriptEl.dataset.sectionId || '').trim();
    if (!sectionId) return;

    var gateKey = '__odxFcTeachRan_' + sectionId;
    if (window[gateKey]) return;
    window[gateKey] = true;

    var thresholdPct = readNumber(scriptEl.dataset.visibilityThreshold, 40);
    var threshold = clamp(thresholdPct / 100, 0.05, 0.95);

    var productMs = readNumber(scriptEl.dataset.productNudgeMs, 1900);
    var imageMs = readNumber(scriptEl.dataset.imageNudgeMs, 1200);

    var sectionRoot = document.getElementById('shopify-section-' + sectionId);
    if (!sectionRoot) return;

    var slider = sectionRoot.querySelector('#Slider-' + sectionId + '.slider--tablet.grid--peek');
    if (!slider) return;

    var slides = Array.from(slider.querySelectorAll('.slider__slide'));
    if (slides.length < 2) return;

    if (!('IntersectionObserver' in window)) {
      // Best-effort fallback: run shortly after load if IO missing (rare).
      window.setTimeout(function () {
        runProductRowTeach(slider, productMs, function () {
          window.setTimeout(function () {
            runInCardNavTeach(sectionRoot, imageMs);
          }, 120);
        });
      }, 350);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i] || !entries[i].isIntersecting) continue;
          io.disconnect();
          runProductRowTeach(slider, productMs, function () {
            window.setTimeout(function () {
              runInCardNavTeach(sectionRoot, imageMs);
            }, 120);
          });
          break;
        }
      },
      { threshold: threshold },
    );

    io.observe(sectionRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
